import { License } from '../types/database';

/**
 * Vérifie si une licence est à vie (pas de date d'expiration)
 */
export const isLifetimeLicense = (license: License): boolean => {
  return license.expires_at === null || license.expires_at === undefined;
};

/**
 * Vérifie si une licence est expirée
 */
export const isLicenseExpired = (license: License): boolean => {
  if (isLifetimeLicense(license)) return false; // Licence à vie = jamais expirée
  if (!license.expires_at) return false;
  
  return new Date(license.expires_at) < new Date();
};

/**
 * Calcule le nombre de jours restants avant expiration
 * Retourne null pour les licences à vie
 */
export const getDaysUntilExpiration = (license: License): number | null => {
  if (isLifetimeLicense(license)) return null;
  if (!license.expires_at) return null;
  
  const now = new Date();
  const expirationDate = new Date(license.expires_at);
  const diffInMs = expirationDate.getTime() - now.getTime();
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffInDays);
};

/**
 * Formate la durée de licence pour l'affichage
 */
export const formatLicenseDuration = (license: License): string => {
  if (isLifetimeLicense(license)) {
    return 'À vie';
  }
  
  const daysLeft = getDaysUntilExpiration(license);
  if (daysLeft === null) return 'Indéterminée';
  if (daysLeft === 0) return 'Expire aujourd\'hui';
  if (daysLeft === 1) return 'Expire demain';
  
  return `${daysLeft} jours restants`;
};

/**
 * Vérifie si une licence est valide (statut actif et non expirée).
 * Ne tient PAS compte des postes utilisés : une licence dont tous les postes
 * sont activés reste valide (voir hasAvailableActivations).
 */
export const isLicenseValid = (license: License): boolean => {
  if (license.status !== 'active') return false;
  if (isLicenseExpired(license)) return false;
  return true;
};

/**
 * Vérifie s'il reste au moins un poste disponible pour activer la licence
 */
export const hasAvailableActivations = (license: License): boolean => {
  if (!license.max_activations) return true;
  return (license.current_activations || 0) < license.max_activations;
};

export type LicenseState = 'active' | 'expired' | 'inactive';

/**
 * État d'affichage d'une licence : active, expirée, ou désactivée (suspendue / révoquée)
 */
export const getLicenseState = (license: License): LicenseState => {
  if (license.status === 'expired') return 'expired';
  if (license.status !== 'active') return 'inactive';
  return isLicenseExpired(license) ? 'expired' : 'active';
};
